import { type FC, useEffect, useState } from 'react';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card/Card';
import { SectionLabel } from '@/components/ui/section-label';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { ToggleChip } from '@/components/ui/toggle-chip';
import { useTranslation } from '@/hooks/useTranslation';
import type { SignalsSeriesPayload } from '../type/SignalsTypes';
import { SignalChartStack } from './SignalChartStack';
import { SignalChartOverlay } from './SignalChartOverlay';
import { SignalSkeleton } from './SignalSkeleton';
import { channelColor, channelLabel } from '../lib/signalChannels';

type ViewMode = 'stack' | 'overlay';

interface SignalsChartCardProps {
  signal: SignalsSeriesPayload | null;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  /** True when `error` is the worker's MissingSignalData error — an empty time window, not a
   * real failure. Renders a friendly hint instead of the generic error+retry card. */
  isEmptyWindow?: boolean;
}

export const SignalsChartCard: FC<SignalsChartCardProps> = ({
  signal,
  isLoading,
  error,
  onRetry,
  isEmptyWindow,
}) => {
  const { t } = useTranslation();
  const [view, setView] = useState<ViewMode>('stack');
  const [visibleTags, setVisibleTags] = useState<Set<string>>(new Set());

  // Reset to "all visible" whenever the set of fetched tags changes (new fetch)
  // — chip choices are client-side only and don't need to survive a refetch.
  const channelTagsKey = signal?.channels.map((c) => c.tag).join('|') ?? '';
  useEffect(() => {
    if (signal) setVisibleTags(new Set(signal.channels.map((c) => c.tag)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelTagsKey]);

  const toggleTag = (tag: string) => {
    setVisibleTags((prev) => {
      if (prev.has(tag)) {
        if (prev.size <= 1) return prev; // at least one channel always stays visible
        const next = new Set(prev);
        next.delete(tag);
        return next;
      }
      const next = new Set(prev);
      next.add(tag);
      return next;
    });
  };

  if (isLoading) {
    return (
      <Card>
        <SignalSkeleton />
      </Card>
    );
  }

  if (error && isEmptyWindow) {
    return (
      <Card>
        <EmptyState
          size="compact"
          title={t('signals.chart.emptyWindowTitle')}
          description={t('signals.chart.emptyWindowHint')}
          data-testid="signals-empty-window-card"
        />
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <EmptyState
          tone="error"
          size="compact"
          title={t('signals.chart.errorTitle')}
          action={
            <Button variant="outline" size="sm" onClick={onRetry}>
              {t('signals.chart.errorRetry')}
            </Button>
          }
        />
      </Card>
    );
  }

  if (!signal) {
    return (
      <EmptyState title={t('signals.empty.title')} description={t('signals.empty.description')} />
    );
  }

  const visibleChannels = signal.channels.filter((channel) => visibleTags.has(channel.tag));
  const samples = signal.channels[0]?.points.length ?? 0;

  // The influx path never returns passes, but always sets aggregateWindowMs — show the
  // effective GROUP BY time() window instead of a now-meaningless "0 passes" count.
  const helperText =
    signal.aggregateWindowMs !== undefined
      ? t('signals.chart.helperAggregated', {
          visible: visibleChannels.length,
          total: signal.channels.length,
          window: signal.aggregateWindowMs,
          samples,
        })
      : t('signals.chart.helper', {
          visible: visibleChannels.length,
          total: signal.channels.length,
          samples,
          passes: signal.passes.length,
        });

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <SectionLabel>{t('signals.chart.badge')}</SectionLabel>
          <span className="text-xs text-muted-foreground">{helperText}</span>
        </div>
        <SegmentedControl
          size="sm"
          aria-label={t('signals.chart.viewLabel')}
          value={view}
          onChange={setView}
          options={[
            { value: 'stack', label: t('signals.chart.viewStack') },
            { value: 'overlay', label: t('signals.chart.viewOverlay') },
          ]}
        />
      </div>

      <div
        className="flex flex-wrap gap-1.5"
        role="group"
        aria-label={t('signals.chart.chipsGroupLabel')}
      >
        {signal.channels.map((channel, idx) => {
          const isVisible = visibleTags.has(channel.tag);
          return (
            <ToggleChip
              key={channel.tag}
              pressed={isVisible}
              data-testid={`signal-chip-${channel.tag}`}
              onClick={() => toggleTag(channel.tag)}
            >
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: channelColor(idx) }}
                aria-hidden
              />
              {channelLabel(channel.tag, t)}
            </ToggleChip>
          );
        })}
      </div>

      {view === 'stack' ? (
        <SignalChartStack channels={visibleChannels} startTime={signal.startTime} />
      ) : (
        <SignalChartOverlay
          channels={visibleChannels}
          passes={signal.passes}
          startTime={signal.startTime}
        />
      )}
    </Card>
  );
};
